import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { UserPermissions } from '~src/interfaces/session.types';

import { AttributeI } from '~src/interfaces/attributes.types';
import { SurveySubjectTypes } from '~src/interfaces/surveys.interfaces';
import LoadingSpinner from '~src/shared/loadingSpinner/LoadingSpinner';
import { ThunkAnyAction } from '~src/services/apiMiddleware';
import { MatrixResponse, SegmentAndAggregations, TopRow } from '~src/interfaces/heatmap.interface';
import Pagination from '~src/shared/lists/pagination/Pagination';
import { SelectedGroupObj } from '~src/store/heatmap/heatmapReducer';
import { HeatmapListFilters } from '~src/modules/heatmap/heatmap.types';
import { StoreI } from '~src/rootReducer';
import { SurveyTypeData } from '~src/modules/surveys/reducers/surveysReducer';
import { translateContent } from '~src/services/translationServices';
import { PeriodValues } from '~src/modules/reports/shared/constants/filterAndSortingKeys';
import {ESTopic} from '~src/interfaces/topic.interface';

import RatingTableInner from './RatingTableInner';
import EntityColumn, { EntityColumnRow } from './column/EntityColumn';
import { InnerSpinner, TableBottom, TableWrapper } from './ratingTable.sc';
import FixedHeaders from './FixedHeaders';
import EmptyTableInner from './EmptyTableInner';
import { ColumnSpinner } from './column/entityColumn.sc';
import {useTranslation} from "react-i18next";

interface OwnProps {
  selectedSurveyType: SurveySubjectTypes;
  selectedAttributeObj: AttributeI | null;
  selectGroup: (groupId: string | null) => Promise<ThunkAnyAction<unknown, unknown> | undefined | void>;
  selectTopic: (topicId: string | null) => Promise<ThunkAnyAction<unknown, unknown> | undefined | void>;
  changePage: (page: number) => void;
  entityLoading: boolean;
  filterState: HeatmapListFilters;
  result: MatrixResponse;
  selectedPeriod: PeriodValues;
  topics: ESTopic[];
}

interface StateProps {
  selectedGroupObj: SelectedGroupObj | null;
  surveysPulse: SurveyTypeData;
  surveysGroup: SurveyTypeData;
  surveysPeople: SurveyTypeData;
  loadingTable: boolean;
  topRow?: SegmentAndAggregations;
  survey?: string;
}

type Props = OwnProps & StateProps & RouteComponentProps<object>;

const RatingTable: FC<Props> = (props) => {
  const [tableLoading, setTableLoading] = useState(false);
  const { t } = useTranslation();

  const selectTopic = (topic: string | null) => {
    setTableLoading(true);
    resetScrolling();
    return props.selectTopic(topic).finally(() => {
      setTableLoading(false);
    });
  };

  const selectGroup = (groupId: string | null = null) => {
    const selectGroupPromise = props.selectGroup(groupId);
    if (selectGroupPromise) {
      setTableLoading(true);
      void selectGroupPromise
        .then(() => {
          resetScrolling();
        })
        .finally(() => {
          setTableLoading(false);
        });
    }
  };

  const scrollRefs = useRef<HTMLDivElement[] | null>(null);

  // scroll function - scrolls all subscribed scroll elements
  const horizontalScroll = (e: Event) => {
    const target = e.target as HTMLDivElement;
    if (target && scrollRefs.current) {
      scrollRefs.current.forEach((ref) => {
        ref.scrollLeft = target.scrollLeft;
      });
    }
  };

  // reset the scrolling of all items back to left = 0
  const resetScrolling = () => {
    scrollElements(0);
  };

  // Subscribe to the horizontal scroll of the inner table
  const addToRef = (el: HTMLDivElement | null) => {
    if (el) {
      el.addEventListener('scroll', horizontalScroll);
      if (scrollRefs.current && scrollRefs.current.indexOf(el) === -1) {
        scrollRefs.current = [...scrollRefs.current, el];
      } else {
        scrollRefs.current = [el];
      }
    }
  };

  const scrollElements = (val: number) => {
    if (scrollRefs.current) {
      scrollRefs.current.forEach((ref) => {
        ref.scrollLeft = val;
      });
    }
  };

  const elWidth = 221;

  const scrollOn = (direction: 'forward' | 'back') => {
    // how many tiles should be scrolled? calculated with the viewport width
    const scrollDist = Math.floor(window.innerWidth * 0.002857142857) * elWidth;
    const curr = scrollRefs.current;
    // get one of the scrolling elements to get the scroll left value
    if (curr) {
      const currentPos = curr.reduce(
        (maxScrollLeft, el) => (el.scrollLeft > maxScrollLeft ? el.scrollLeft : maxScrollLeft),
        0,
      );
      const currentOffset = currentPos % scrollDist;
      if (direction === 'forward') {
        scrollTable(currentPos, scrollDist - currentOffset, scrollDist);
      } else {
        scrollTable(currentPos, -(currentOffset < 1 ? scrollDist : currentOffset), scrollDist);
      }
    }
  };

  const scrollInterval = useRef<NodeJS.Timeout | null>(null);

  // interval cleanup on unmounting
  useEffect(
    () => () => {
      if (scrollInterval.current) clearTimeout(scrollInterval.current);
    },
    [],
  );

  const scrollTable = (currentPos: number, val: number, scrollDist: number) => {
    const currentInterval = scrollInterval.current;
    if (currentInterval) {
      clearTimeout(currentInterval);
    }
    if (scrollRefs.current) {
      let count = 0;
      // cut the scrolling distance in 60 pieces ( 60 fps ) minus the value that is already scrolled
      const maxVal = 60 * (Math.abs(val) / scrollDist);
      const step = val / maxVal;
      const counter = setInterval(() => {
        if (scrollRefs.current) {
          if (count > maxVal) {
            // position the element in final position
            scrollElements(currentPos + val);
            clearTimeout(counter);
          } else {
            // scroll the element one step every 5 ms ( 60 pieces * 5ms = 300ms animation time )
            scrollElements(currentPos + count * step);
            count++;
          }
        }
      }, 5);
      // put the interval in the container
      scrollInterval.current = counter;
    }
  };

  const selectedSurveyName: string = useMemo(() => {
    if (props.selectedSurveyType === SurveySubjectTypes.People) {
      if (props.filterState.surveyPeople) {
        let survey = props.surveysPeople.items.find(
          tmpSurvey => tmpSurvey.id === props.filterState.surveyPeople,
        );
        if (!survey) {
          survey = props.surveysPeople.itemsSearch.find(
            tmpSurvey => tmpSurvey.id === props.filterState.surveyPeople,
          );
        }
        if (survey) {
          return translateContent(survey.name);
        }
      }
    } else if (props.selectedSurveyType === SurveySubjectTypes.Groups) {
      if (props.filterState.surveyGroup) {
        let survey = props.surveysGroup.items.find(
          tmpSurvey => tmpSurvey.id === props.filterState.surveyGroup,
        );
        if (!survey) {
          survey = props.surveysGroup.itemsSearch.find(
            tmpSurvey => tmpSurvey.id === props.filterState.surveyGroup,
          );
        }
        if (survey) {
          return translateContent(survey.name);
        }
      }
    } else {
      if (props.filterState.surveyPulse) {
        let survey = props.surveysPulse.items.find(
          tmpSurvey => tmpSurvey.id === props.filterState.surveyPulse,
        );
        if (!survey) {
          survey = props.surveysPulse.itemsSearch.find(
            tmpSurvey => tmpSurvey.id === props.filterState.surveyPulse,
          );
        }
        if (survey) {
          return translateContent(survey.name);
        }
      }
    }
    return t(`x.compare.header.filters.select_${props.selectedSurveyType}_survey.default_option`);
  }, [
    props.surveysGroup,
    props.filterState,
    props.selectedSurveyType,
    props.surveysPulse,
    props.surveysPeople,
  ]);

  const entityColumnTopRow: EntityColumnRow | undefined = useMemo(() => {
    const topRowSegment = props.topRow && props.topRow.segment;
    return topRowSegment
      ? {
          segment: topRowSegment,
          hasAggregations: Boolean(props.topRow && props.topRow.agg),
        }
      : undefined;
  }, [props.topRow]);

  const entityColumnRows: EntityColumnRow[] = useMemo(() => {
    if (props.result && props.result.segmById) {
      return Object.values(props.result.segmById).map((segment) => {
        return { segment, hasAggregations: Boolean(props.result.aggrBySegmentId[segment.id]) };
      });
    }
    return [];
  }, [props.result.segmById]);

  const selectedGroupRow: EntityColumnRow | undefined = useMemo(() => {
    if (!props.selectedGroupObj) {
      return undefined;
    }
    return {
      segment: props.selectedGroupObj.segment,
      hasAggregations: Object.values(props.selectedGroupObj.aggregation).length > 0,
    };
  }, [props.selectedGroupObj]);

  return (
    <TableWrapper>
      <FixedHeaders
        topics={props.topics}
        selectedPeriod={props.selectedPeriod}
        selectedTopic={props.filterState.filterTopicId}
        selectedAttributeObj={props.selectedAttributeObj}
        selectedSurveyName={selectedSurveyName}
        selectTopic={selectTopic}
        scrollOn={scrollOn}
        addToRef={addToRef}
      />

      <TableBottom>
        {props.entityLoading ? (
          <ColumnSpinner>
            <LoadingSpinner />
          </ColumnSpinner>
        ) : (
          <EntityColumn
            aggrBySegmentId={props.result.aggrBySegmentId}
            segments={entityColumnRows}
            selectGroup={selectGroup}
            selectedGroup={selectedGroupRow}
            surveyType={props.selectedSurveyType}
            topRow={entityColumnTopRow}
          />
        )}

        {props.loadingTable || tableLoading ? (
          <InnerSpinner>
            <LoadingSpinner />
          </InnerSpinner>
        ) : props.topics.length === 0 ? (
          <EmptyTableInner
            messageKey={`x.matrix.table.inner.${'no_topics'}`}
            link="content/surveys"
            linkKey={`x.matrix.table.inner.${'no_topics'}.link`}
          />
        ) : Object.keys(props.result.segmById).length < 1 ? (
          <EmptyTableInner
            messageKey="x.compare.table.no_segments"
            link="company/attributes"
            linkKey="x.compare.table.no_segments.link"
          />
        ) : (
          <RatingTableInner
            selectedTopicId={props.filterState.filterTopicId}
            addToRef={addToRef}
            selectedPeriod={props.selectedPeriod}
            topics={props.topics}
            filterSurvey={
              props.filterState.surveyPeople ||
              props.filterState.surveyGroup ||
              props.filterState.surveyPulse ||
              null
            }
          />
        )}
      </TableBottom>

      {!props.loadingTable && (
        <Pagination
          t={t}
          totalItems={props.result.pagination.total}
          pageSize={props.result.pagination.per_page}
          onChange={props.changePage}
          marginTop={60}
          history={props.history}
          location={props.location}
          defaultCurrPage={props.result.pagination.page}
        />
      )}
    </TableWrapper>
  );
};

const mapStateToProps = (state: StoreI): StateProps => {
  let topRow: TopRow | undefined;
  if (state.user.session.user?.userPermissions.includes(UserPermissions.ManageCompany)) {
    topRow = state.heatmap.topRow;
  }
  return {
    surveysPulse: state.surveyManagement.surveys.general,
    surveysGroup: state.surveyManagement.surveys.groups,
    surveysPeople: state.surveyManagement.surveys.people,
    loadingTable: state.heatmap.loadingTable,
    topRow: topRow,
    selectedGroupObj: state.heatmap.selectedGroup,
    survey: state.heatmap.survey,
  };
};

const RatingTableWithRouter = withRouter(RatingTable);
const RatingTableSuper = connect(mapStateToProps)(RatingTableWithRouter);

export default RatingTableSuper;
